import { useState, useMemo } from 'react';
import {
  FileText, Plus, DollarSign, Calendar, MapPin, Edit2, Trash2, X, ChevronLeft, ChevronRight, Search, Landmark, Percent, Clock
} from 'lucide-react';
import { useContratos, useComercialMutations } from '../../api/hooks/useComercial';
import { Modal } from '../../components/ui/Modal';
import { useCrud } from '../../api/hooks/useCrud';
import '../../components/ui/datatable.css';

const STATUS_STYLES: Record<string, { color: string; bg: string }> = {
  ativo: { color: '#10b981', bg: 'rgba(16,185,129,0.08)' },
  suspenso: { color: '#f59e0b', bg: 'rgba(245,158,11,0.08)' },
  encerrado: { color: '#64748b', bg: 'var(--bg-muted, #f1f5f9)' },
  rescindido: { color: '#ef4444', bg: 'rgba(239,68,68,0.08)' }
};

export function ContratosPage() {
  const [page, setPage] = useState(1);
  const [busca, setBusca] = useState('');
  const [tipoFilter, setTipoFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedContrato, setSelectedContrato] = useState<any | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const limite = 20;

  const { data, isLoading, refetch } = useContratos({
    page,
    limite,
    busca: busca || undefined,
    tipo: tipoFilter || undefined,
    status: statusFilter || undefined,
  });

  const { criarContrato, atualizarContrato, excluirContrato } = useComercialMutations();

  const { data: municipios } = useCrud<any>('admin/municipios');
  const { data: estados } = useCrud<any>('admin/estados');
  const { data: redes } = useCrud<any>('admin/redes');

  const handleOpenCreate = () => {
    setSelectedContrato(null);
    setShowModal(true);
  };

  const handleOpenEdit = (ct: any) => {
    setSelectedContrato(ct);
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (confirm('Tem certeza que deseja excluir este contrato? (Esta ação desativará o contrato no sistema)')) {
      try {
        await excluirContrato.mutateAsync(id);
        refetch();
      } catch (err) {
        console.error(err);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    const formData = new FormData(e.currentTarget);

    const payload = {
      numero: formData.get('numero') as string,
      titulo: formData.get('titulo') as string,
      tipo: formData.get('tipo') as string,
      municipioId: formData.get('municipioId') as string || undefined,
      estadoId: formData.get('estadoId') as string || undefined,
      redeId: formData.get('redeId') as string || undefined,
      valorTotal: Number(formData.get('valorTotal')),
      dataInicio: new Date(formData.get('dataInicio') as string).toISOString(),
      dataFim: new Date(formData.get('dataFim') as string).toISOString(),
      status: formData.get('status') as string,
      observacoes: formData.get('observacoes') as string || undefined,
    };

    try {
      if (selectedContrato) {
        await atualizarContrato.mutateAsync({ id: selectedContrato.id, ...payload });
      } else {
        await criarContrato.mutateAsync(payload);
      }
      setShowModal(false);
      refetch();
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };



  // Stats calculation
  const totalContratos = data?.total || 0;
  const totalPages = data?.totalPages || 1;
  const totalValue = useMemo(() => (data?.items || []).reduce((acc: number, ct: any) => acc + Number(ct.valorTotal || 0), 0), [data]);
  const ativosCount = useMemo(() => (data?.items || []).filter((ct: any) => ct.status === 'ativo').length, [data]);

  return (
    <div className="admin-page animate-fadeIn contratos-page-compact" style={{ marginTop: -22, height: 'calc(100vh - 116px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      
      {/* ═══ HERO BANNER PREMIUM ═══ */}
      <div style={{
        background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 40%, #14b8a6 100%)',
        borderRadius: 18, padding: 'clamp(12px, 1.4vw, 18px)', marginBottom: 12, position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -60, right: -60, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,.07)' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 2 }}>
                <FileText size={16} style={{ color: '#99f6e4' }} />
                <span style={{ color: 'rgba(255,255,255,.45)', fontSize: '.66rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.08em' }}>Comercial</span>
              </div>
              <h1 style={{ color: 'white', fontSize: 'clamp(1.05rem, 1.6vw, 1.28rem)', fontWeight: 800, margin: 0 }}>Contratos Comerciais</h1>
              <p style={{ color: 'rgba(255,255,255,.55)', fontSize: '.75rem', marginTop: 2 }}>
                Gestão e vigência dos acordos comerciais ativos com redes de ensino municipais e estaduais
              </p>
            </div>
            
            <button onClick={handleOpenCreate} style={{
              display: 'flex', alignItems: 'center', gap: 7, padding: '8px 14px', borderRadius: 9,
              background: '#99f6e4', color: '#0f766e', fontWeight: 700, fontSize: '.8rem', border: 'none', cursor: 'pointer',
            }}>
              <Plus size={14} /> Novo Contrato
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 10, marginTop: 12 }}>
            {[
              { icon: FileText, value: totalContratos, label: 'Total Contratos', color: '#99f6e4' },
              { icon: DollarSign, value: new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(totalValue), label: 'Faturamento Anual', color: '#34d399' },
              { icon: Percent, value: ativosCount, label: 'Contratos Ativos', color: '#fbbf24' },
              { icon: Clock, value: (data?.items || []).filter((ct: any) => ct.status === 'suspenso').length, label: 'Suspensos', color: '#f472b6' },
            ].map((s, i) => (
              <div key={i} className="kb-stat-glass">
                <div className="kb-stat-icon" style={{ background: `${s.color}15`, color: s.color }}>
                  <s.icon size={16} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div className="kb-stat-value">{s.value}</div>
                  <div className="kb-stat-label">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FILTER BAR & TABLE CONTAINER */}
      <div className="data-table-wrapper" style={{ flex: 1, minHeight: 0 }}>
        
        {/* Filters bar */}
        <div className="dt-toolbar" style={{ justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <div className="dt-search">
              <Search size={14} className="dt-search-icon" />
              <input
                value={busca}
                onChange={(e) => { setBusca(e.target.value); setPage(1); }}
                placeholder="Buscar por nº ou título..."
                className="dt-search-input"
              />
            </div>
            
            <select
              value={tipoFilter}
              onChange={(e) => { setTipoFilter(e.target.value); setPage(1); }}
              className="form-select"

            >
              <option value="">Todos os tipos</option>
              <option value="municipal">Municipal</option>
              <option value="estadual">Estadual</option>
              <option value="privado">Privado</option>
            </select>

            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="form-select"

            >
              <option value="">Todos os status</option>
              <option value="ativo">Ativo</option>
              <option value="suspenso">Suspenso</option>
              <option value="encerrado">Encerrado</option>
              <option value="rescindido">Rescindido</option>
            </select>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="dt-page-info">{totalContratos} registro(s)</span>
          </div>
        </div>

        {/* Content list/table scroll */}
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <div className="dt-container custom-scroll">
            {isLoading ? (
              <div className="dt-loading">Carregando...</div>
            ) : !data?.items || data.items.length === 0 ? (
              <div className="dt-empty">
                Nenhum contrato encontrado.
              </div>
            ) : (
              <table className="dt-table">
                <thead>
                  <tr>
                    <th>Nº Contrato</th>
                    <th>Título</th>
                    <th>Tipo</th>
                    <th>Status</th>
                    <th>Valor Total</th>
                    <th>Contratante</th>
                    <th>Vigência</th>
                    <th style={{ textAlign: 'right' }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((ct: any) => {
                    const ss = STATUS_STYLES[ct.status] || STATUS_STYLES.ativo;
                    return (
                      <tr key={ct.id} className="dt-row">
                        <td style={{ fontFamily: 'monospace', fontWeight: 700 }}>{ct.numero}</td>
                        <td style={{ fontWeight: 700 }}>{ct.titulo}</td>
                        <td style={{ textTransform: 'capitalize' }}>{ct.tipo}</td>
                        <td>
                          <span className="badge" style={{ background: ss.bg, color: ss.color }}>
                            {ct.status.toUpperCase()}
                          </span>
                        </td>
                        <td style={{ fontWeight: 700 }}>
                          {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(ct.valorTotal))}
                        </td>
                        <td>
                          {ct.municipio?.nome || ct.estado?.nome || ct.rede?.nome || '-'}
                          {ct.estado && <span style={{ color: 'var(--text-muted)' }}> ({ct.estado.sigla})</span>}
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem' }}>
                            <Calendar size={12} style={{ color: 'var(--text-muted)' }} />
                            <span>
                              {new Date(ct.dataInicio).toLocaleDateString('pt-BR')} - {new Date(ct.dataFim).toLocaleDateString('pt-BR')}
                            </span>
                          </div>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <div style={{ display: 'inline-flex', gap: 6 }}>
                            <button className="action-btn" onClick={() => handleOpenEdit(ct)} title="Editar" style={{ color: '#f59e0b' }}>
                              <Edit2 size={15} />
                            </button>
                            <button className="action-btn action-btn-danger" onClick={() => handleDelete(ct.id)} title="Excluir">
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Footer Pagination */}
          <div className="dt-pagination">
            <span className="dt-page-info">
              Página {page} de {totalPages}
            </span>
            <div className="dt-page-buttons">
              <button className="dt-page-btn" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>
                <ChevronLeft size={14} /> Anterior
              </button>
              <button className="dt-page-btn" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
                Próximo <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Form Modal */}
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title=""
        size="lg"
        contentClassName="umu-wizard-modal"
        footer={
          <div style={{ display: 'flex', gap: 10, width: '100%', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={() => setShowModal(false)}
              className="btn-cancel"
            >
              Cancelar
            </button>
            <button
              type="submit"
              form="contrato-form"
              disabled={isSubmitting}
              className="btn-save"

            >
              {isSubmitting ? 'Salvando...' : 'Salvar Contrato'}
            </button>
          </div>
        }
      >
        <form id="contrato-form" onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          {/* Banner */}
          <div style={{
            background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 40%, #14b8a6 100%)',
            borderRadius: '12px 12px 0 0', padding: '20px 24px 16px', position: 'relative', overflow: 'hidden',
          }}>
            <div style={{ position: 'absolute', top: -40, right: -40, width: 140, height: 140, borderRadius: '50%', background: 'rgba(255,255,255,.05)' }} />
            <div style={{ position: 'relative', zIndex: 1 }}>
              <h2 style={{ color: 'white', fontSize: '1.1rem', fontWeight: 800, margin: '0 0 4px' }}>
                {selectedContrato ? 'Editar Contrato' : 'Novo Contrato'}
              </h2>
              <p style={{ color: 'rgba(255,255,255,.55)', fontSize: '.78rem', margin: 0 }}>
                {selectedContrato ? 'Atualize os dados e vigência do contrato selecionado.' : 'Cadastre um novo contrato na plataforma Aluno Conectado.'}
              </p>
            </div>
          </div>

          {/* Body Fields (2-Column Grid) */}
          <div style={{
            padding: '20px 24px',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '12px 16px',
            flex: '1 1 auto',
            minHeight: 0,
            maxHeight: 'min(580px, 72vh)',
            overflowY: 'auto'
          }}>
            <div className="form-group">
              <label className="form-label">Número do Contrato *</label>
              <input name="numero" defaultValue={selectedContrato?.numero || ''} required className="form-input" placeholder="Ex: CT-2026/001" />
            </div>

            <div className="form-group">
              <label className="form-label">Título *</label>
              <input name="titulo" defaultValue={selectedContrato?.titulo || ''} required className="form-input" placeholder="Ex: Licenciamento AC" />
            </div>

            <div className="form-group">
              <label className="form-label">Tipo Contrato *</label>
              <select name="tipo" defaultValue={selectedContrato?.tipo || 'municipal'} className="form-select">
                <option value="municipal">Municipal</option>
                <option value="estadual">Estadual</option>
                <option value="privado">Privado</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Status *</label>
              <select name="status" defaultValue={selectedContrato?.status || 'ativo'} className="form-select">
                <option value="ativo">Ativo</option>
                <option value="suspenso">Suspenso</option>
                <option value="encerrado">Encerrado</option>
                <option value="rescindido">Rescindido</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Vigência (Início) *</label>
              <input name="dataInicio" type="date" defaultValue={selectedContrato?.dataInicio ? selectedContrato.dataInicio.split('T')[0] : ''} required className="form-input" />
            </div>

            <div className="form-group">
              <label className="form-label">Vigência (Fim) *</label>
              <input name="dataFim" type="date" defaultValue={selectedContrato?.dataFim ? selectedContrato.dataFim.split('T')[0] : ''} required className="form-input" />
            </div>

            <div className="form-group">
              <label className="form-label">Valor Total (R$) *</label>
              <input name="valorTotal" type="number" step="0.01" defaultValue={selectedContrato?.valorTotal || ''} required className="form-input" placeholder="0.00" />
            </div>

            <div className="form-group">
              <label className="form-label">Rede Escolar (opcional)</label>
              <select name="redeId" defaultValue={selectedContrato?.redeId || ''} className="form-select">
                <option value="">Selecione a rede...</option>
                {(redes || []).map((r: any) => (
                  <option key={r.id} value={r.id}>{r.nome}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Município (opcional)</label>
              <select name="municipioId" defaultValue={selectedContrato?.municipioId || ''} className="form-select">
                <option value="">Selecione o município...</option>
                {(municipios || []).map((m: any) => (
                  <option key={m.id} value={m.id}>{m.nome}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Estado (opcional)</label>
              <select name="estadoId" defaultValue={selectedContrato?.estadoId || ''} className="form-select">
                <option value="">Selecione o estado...</option>
                {(estados || []).map((e: any) => (
                  <option key={e.id} value={e.id}>{e.nome} ({e.sigla})</option>
                ))}
              </select>
            </div>

            <div className="form-group" style={{ gridColumn: 'span 2' }}>
              <label className="form-label">Observações</label>
              <textarea name="observacoes" defaultValue={selectedContrato?.observacoes || ''} rows={2} className="form-input" style={{ resize: 'vertical' }} placeholder="Detalhes do contrato, termos, link..." />
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
}
